import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import type { Connection, CreateTarget, ProviderInfo, Task } from '../types';

interface Props {
  connections: Connection[];
  providers: ProviderInfo[];
  fetchTargets: (connectionId: string) => Promise<CreateTarget[]>;
  onCreate: (
    connectionId: string,
    body: { targetId: string; title: string; description: string | null; dueDate: string | null }
  ) => Promise<Task>;
  onCreated: (task: Task) => void;
  onClose: () => void;
}

/**
 * Creates a task directly in an external system (e.g. a specific Monday
 * board), rather than in Z3NTRAFY alone. Distinct from the plain "+ New task"
 * flow, which only ever creates a local, unsynced task.
 */
export function CreateExternalTaskModal({ connections, providers, fetchTargets, onCreate, onCreated, onClose }: Props) {
  const eligible = connections.filter(
    (c) => c.enabled && providers.find((p) => p.id === c.provider)?.supportsCreate
  );

  const [connectionId, setConnectionId] = useState(eligible[0]?.id ?? '');
  const [targets, setTargets] = useState<CreateTarget[] | null>(null);
  const [targetId, setTargetId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connection = eligible.find((c) => c.id === connectionId);

  useEffect(() => {
    if (!connectionId) return;
    setTargets(null);
    setTargetId('');
    setError(null);
    fetchTargets(connectionId)
      .then((t) => {
        setTargets(t);
        if (t.length === 1) setTargetId(t[0].id);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load boards for this system.'));
  }, [connectionId, fetchTargets]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  async function submit() {
    if (!connection || !targetId || !title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const task = await onCreate(connection.id, {
        targetId,
        title: title.trim(),
        description: description.trim() || null,
        dueDate: dueDate || null,
      });
      onCreated(task);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  }

  if (eligible.length === 0) {
    return (
      <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal" role="dialog" aria-modal="true" aria-label="Create in a connected system">
          <div className="modal-head">
            <h3>Create in a connected system</h3>
          </div>
          <div className="modal-body">
            <p className="help">
              None of your connected systems support creating tasks from Z3NTRAFY yet. Connect one that does (like
              Monday.com) under <strong>Systems</strong>, then try again.
            </p>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Create in a connected system">
        <div className="modal-head">
          <h3>Create in a connected system</h3>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="modal-body form-grid">
            {error && <div className="notice error">{error}</div>}
            <div className="notice info">
              <span>This creates a real item in the source system, assigned to you there, and links it back here.</span>
            </div>

            <div className="two-col">
              <div className="form-row">
                <label htmlFor="ext-connection">System</label>
                <select
                  id="ext-connection"
                  value={connectionId}
                  onChange={(e) => setConnectionId(e.target.value)}
                  disabled={saving}
                >
                  {eligible.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="ext-target">Board</label>
                <select
                  id="ext-target"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  disabled={saving || targets === null}
                  required
                >
                  <option value="">{targets === null ? 'Loading…' : 'Choose one…'}</option>
                  {targets?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {targets?.length === 0 && (
                  <span className="help">No boards visible to this connection's API token.</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="ext-title">Title</label>
              <input
                id="ext-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
                disabled={saving}
              />
            </div>

            <div className="form-row">
              <label htmlFor="ext-desc">Description</label>
              <textarea id="ext-desc" value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
            </div>

            <div className="form-row">
              <label htmlFor="ext-due">Deadline</label>
              <input
                id="ext-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={saving || !targetId || !title.trim()}>
              {saving && <span className="spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
